import { Anchor, atAnchor, Point, Rect } from '@st/geom'
import { getTargetScrollPosition } from '@st/geom/viewport'
import type { ScrollGeometry } from './viewport'

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function normalizePoint(point: Point, rect: Rect): Point | null {
  if (rect.width <= 0 || rect.height <= 0) {
    return null
  }

  return {
    x: (point.x - rect.x) / rect.width,
    y: (point.y - rect.y) / rect.height
  }
}

/**
 * After a layout change (zoom, page insert/delete, resize), find the scroll
 * position that keeps the same logical document point at the viewport center.
 */
export function resolvePinnedViewportCenterScrollPosition(
  prev: ScrollGeometry,
  next: ScrollGeometry
): Point | null {
  const prevCenter = atAnchor(prev.viewportRect, Anchor.center)
  const normalizedCenter = normalizePoint(prevCenter, prev.scrollAreaRect)
  if (!normalizedCenter) {
    return null
  }

  const targetPoint = {
    x: next.scrollAreaRect.x + normalizedCenter.x * next.scrollAreaRect.width,
    y: next.scrollAreaRect.y + normalizedCenter.y * next.scrollAreaRect.height
  }

  const targetScrollPosition = getTargetScrollPosition({
    geom: {
      viewportRect: next.viewportRect,
      scrollAreaRect: next.scrollAreaRect,
      childRect: {
        x: targetPoint.x,
        y: targetPoint.y,
        width: 0,
        height: 0
      }
    },
    viewportAnchor: Anchor.center,
    childAnchor: Anchor.center
  })

  const maxScrollLeft = Math.max(next.scrollAreaRect.width - next.viewportRect.width, 0)
  const maxScrollTop = Math.max(next.scrollAreaRect.height - next.viewportRect.height, 0)

  return {
    x: clamp(targetScrollPosition.x, 0, maxScrollLeft),
    y: clamp(targetScrollPosition.y, 0, maxScrollTop)
  }
}
